import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Check, Loader2 } from "@/components/icons";
import { ThemeCardPreview } from "@/components/setup/theme-card-preview";
import type { Template } from "@/lib/types";
import { cn } from "@/lib/utils";

/** Curated display order (not alphabetical) — templates aren't returned
 *  in a fixed order server-side. Anything not listed here (a new
 *  template) sorts after these, in whatever order it arrived in. */
const DISPLAY_ORDER = ["Atelier", "Noir", "Evergreen Ivory", "Evergreen", "Keepsake", "Darkroom"];

function byDisplayOrder(a: Template, b: Template): number {
  const ai = DISPLAY_ORDER.indexOf(a.name);
  const bi = DISPLAY_ORDER.indexOf(b.name);
  return (ai === -1 ? DISPLAY_ORDER.length : ai) - (bi === -1 ? DISPLAY_ORDER.length : bi);
}

/**
 * Props-driven template grid for the buyer wizard. Each card is a live
 * preview (see ThemeCardPreview): its own real background/text colors and
 * its own heading + body fonts (Google Fonts loaded on mount), with the
 * couple's own name as the heading sample — so picking a look means
 * *seeing* it, not reading a font name. Data comes from getMySetup (no
 * admin callable); picking is delegated to the parent.
 */
export function SimpleTemplateGrid({
  templates,
  activeId,
  pendingId,
  disabled,
  sampleName,
  onPick,
}: {
  templates: Template[];
  activeId: string | null;
  pendingId: string | null;
  disabled?: boolean;
  /** Couple's display name, shown as the live heading sample. */
  sampleName?: string | null;
  onPick: (template: Template) => void;
}) {
  const { t } = useTranslation();
  // Every card renders simultaneously, so every template's fonts load at
  // once — one stylesheet per template's prebuilt cssUrl, cleaned up
  // together on unmount (same singleton-link pattern as FontPreview).
  useEffect(() => {
    const ids = templates.map((template) => `template-font-${template.id}`);
    for (const template of templates) {
      const id = `template-font-${template.id}`;
      let link = document.getElementById(id) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }
      if (link.href !== template.fontScheme.cssUrl) {
        link.href = template.fontScheme.cssUrl;
      }
    }
    return () => {
      for (const id of ids) document.getElementById(id)?.remove();
    };
  }, [templates]);

  if (templates.length === 0) return null;
  const heading = sampleName?.trim() || t("setup.yourNames");
  const orderedTemplates = [...templates].sort(byDisplayOrder);

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {orderedTemplates.map((template) => {
        const active = template.id === activeId;
        const busy = pendingId === template.id;
        return (
          <button
            key={template.id}
            type="button"
            aria-pressed={active}
            disabled={disabled}
            onClick={() => onPick(template)}
            className={cn(
              "overflow-hidden rounded-lg border text-left transition-shadow disabled:opacity-70",
              active ? "border-primary ring-1 ring-primary" : "border-border hover:shadow-md",
            )}
          >
            <ThemeCardPreview
              palette={template.palette}
              fontScheme={template.fontScheme}
              heading={heading}
              label={template.name}
              badge={
                (busy || active) && (
                  <div className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-background/90 shadow">
                    {busy ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    ) : (
                      <Check className="h-3.5 w-3.5 text-primary" />
                    )}
                  </div>
                )
              }
            />
          </button>
        );
      })}
    </div>
  );
}
